// src/pages/Warehouse/Incoming/IncomingTable.jsx
import React, { useEffect, useMemo, useState } from "react"
import {
  Table,
  Space,
  DatePicker,
  Input,
  Button,
  Typography,
  Divider,
  Row,
  Col,
  message,
  Popconfirm,
} from "antd"
import dayjs from "dayjs"
import {
  collection,
  onSnapshot,
  query,
  where,
  orderBy,
  Timestamp,
  deleteDoc,
  doc,
  getDocs,
} from "firebase/firestore"
import { SearchOutlined, ReloadOutlined, DeleteOutlined } from "@ant-design/icons"
import { db, storage } from "../../../../firebase/config"
import AddIncomingItem from "./AddIncomingItem"
import { ref as sRef, deleteObject } from "firebase/storage"
import EditIncomingItemDrawer from "./EditIncomingItemDrawer"
import { logRollDeletedEvent, logInventoryDeletedEvent } from "../Helper/warehouseEvents"

const { RangePicker } = DatePicker
const { Text, Title } = Typography

const nf = new Intl.NumberFormat("en-US")
const cf = new Intl.NumberFormat("en-US", { style: "currency", currency: "EUR" })

const BLOCKED_USER_UID = "E1XNaWvT6PccXRSKB3JNlNehI362"
const PRIVILEGED_ROLES = new Set(["admin", "accountant"])

const toDay = (v) => {
  if (!v) return null
  if (v?.seconds != null) return dayjs.unix(v.seconds)
  if (typeof v?.toDate === "function") return dayjs(v.toDate())
  return dayjs(v)
}

export default function IncomingTable({ currentUserId = null, roles = [] }) {
  const [loading, setLoading] = useState(false)
  const [dateRange, setDateRange] = useState(() => [dayjs().startOf("day"), dayjs().endOf("day")])
  const [search, setSearch] = useState("")
  const [rows, setRows] = useState([])

  const canSeeAll = roles?.some((r) => PRIVILEGED_ROLES.has(String(r).toLowerCase()))

  useEffect(() => {
    setLoading(true)
    const baseRef = collection(db, "warehouseIncoming")
    const constraints = [orderBy("arrivedAt", "desc")]

    if (dateRange?.[0] && dateRange?.[1]) {
      const start = Timestamp.fromDate(dateRange[0].startOf("day").toDate())
      const end = Timestamp.fromDate(dateRange[1].endOf("day").toDate())
      constraints.push(where("arrivedAt", ">=", start))
      constraints.push(where("arrivedAt", "<=", end))
    }

    const qref = query(baseRef, ...constraints)
    const unsub = onSnapshot(
      qref,
      (snap) => {
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
        setRows(data)
        setLoading(false)
      },
      () => setLoading(false)
    )

    return () => unsub()
  }, [dateRange])

  const searchedRows = useMemo(() => {
    const q = (search || "").trim().toLowerCase()
    if (!q) return rows
    return rows.filter((r) => {
      const hay = [
        r.productName,
        r.notes,
        r.supplierName,
        r.productTypeName,
        r.barcode,
        r.ean,
        r.batchSeries,
        r.manufacturerCode,
        r.materialName,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
      return hay.includes(q)
    })
  }, [rows, search])

  const visibleRows = useMemo(() => {
    if (canSeeAll) return searchedRows
    return searchedRows.filter((r) => r.user !== BLOCKED_USER_UID)
  }, [searchedRows, canSeeAll])

  const uniq = (arr) => Array.from(new Set(arr.filter(Boolean)))
  const supplierFilters = uniq(visibleRows.map((r) => r.supplierName || r.supplierId)).map((v) => ({
    text: v,
    value: v,
  }))
  const typeFilters = uniq(visibleRows.map((r) => r.productTypeName || r.productTypeId)).map(
    (v) => ({ text: v, value: v })
  )
  const unitFilters = uniq(visibleRows.map((r) => r.unit)).map((v) => ({ text: v, value: v }))
  const materialFilters = uniq(visibleRows.map((r) => r.materialName || r.material || null)).map(
    (v) => ({ text: v, value: v })
  )

  const showMaterial = visibleRows.some((r) => !!(r.materialName || r.material))

  const handleDelete = async (record) => {
    try {
      // 1. Delete invoice file if exists
      if (record.invoiceStoragePath) {
        try {
          await deleteObject(sRef(storage, record.invoiceStoragePath))
        } catch {
          /* ignore */
        }
      }

      // 2. Find and delete from rolls or inventory
      const isRoll = record.productTypeName?.toLowerCase().includes("roll")

      if (isRoll) {
        const queries = []

        if (record.barcode) {
          queries.push(query(collection(db, "rolls"), where("barcode", "==", record.barcode)))
        }

        if (record.ean) {
          queries.push(query(collection(db, "rolls"), where("ean", "==", record.ean)))
        }

        for (const q of queries) {
          try {
            const rollsSnap = await getDocs(q)
            for (const rollDoc of rollsSnap.docs) {
              const rollData = rollDoc.data()

              const rollArrived = toDay(rollData.receivedInStock)
              const incomingArrived = toDay(record.arrivedAt)
              if (rollArrived?.isSame(incomingArrived, "day")) {
                await logRollDeletedEvent({
                  rollDoc: rollData,
                  rollDocId: rollDoc.id,
                  userId: currentUserId,
                })
                await deleteDoc(doc(db, "rolls", rollDoc.id))
              }
            }
          } catch (err) {
            console.error("Error deleting roll:", err)
          }
        }
      } else {
        const invQuery = query(
          collection(db, "warehouseInventory"),
          where("productName", "==", record.productName || "")
        )

        try {
          const invSnap = await getDocs(invQuery)
          for (const invDoc of invSnap.docs) {
            const invData = invDoc.data()

            const invArrived = toDay(invData.arrivedAt)
            const incomingArrived = toDay(record.arrivedAt)
            if (
              invArrived?.isSame(incomingArrived, "day") &&
              invData.supplierId === record.supplierId
            ) {
              await logInventoryDeletedEvent({
                inventoryDoc: invData,
                inventoryDocId: invDoc.id,
                userId: currentUserId,
              })
              await deleteDoc(doc(db, "warehouseInventory", invDoc.id))
            }
          }
        } catch (err) {
          console.error("Error deleting inventory:", err)
        }
      }

      // 3. Delete related events from warehouseEvents
      try {
        const eventsQuery = query(
          collection(db, "warehouseEvents"),
          where("metadata.sourceDocId", "==", record.id)
        )
        const eventsSnap = await getDocs(eventsQuery)

        for (const eventDoc of eventsSnap.docs) {
          await deleteDoc(doc(db, "warehouseEvents", eventDoc.id))
        }
      } catch (err) {
        console.error("Error deleting events:", err)
      }

      // 4. Delete from warehouseIncoming
      await deleteDoc(doc(db, "warehouseIncoming", record.id))

      message.success("Item deleted from all collections and events")
    } catch (e) {
      console.error(e)
      message.error("Failed to delete")
    }
  }

  const baseColumns = [
    {
      title: "Supplier",
      dataIndex: "supplierName",
      filters: supplierFilters,
      onFilter: (value, record) =>
        (record.supplierName || record.supplierId || "").toString() === value,
      render: (val, record) => val || record.supplierId || <Text type="secondary">—</Text>,
    },
    {
      title: "Product type",
      dataIndex: "productTypeName",
      filters: typeFilters,
      onFilter: (value, record) =>
        (record.productTypeName || record.productTypeId || "").toString() === value,
      render: (val, record) => val || record.productTypeId || <Text type="secondary">—</Text>,
    },
    { title: "Product name", dataIndex: "productName", ellipsis: true, width: 300 },
    { title: "Width", dataIndex: "materialWidth", ellipsis: true, width: 100 },
    {
      title: "Unit",
      dataIndex: "unit",
      width: 90,
      align: "center",
      filters: unitFilters,
      onFilter: (value, record) => (record.unit || "").toString() === value,
    },
    {
      title: "Quantity",
      dataIndex: "amount",
      width: 120,
      align: "right",
      sorter: (a, b) => (Number(a.amount) || 0) - (Number(b.amount) || 0),
      render: (v) => (Number.isFinite(Number(v)) ? nf.format(Number(v)) : "—"),
    },
    {
      title: "Price",
      dataIndex: "price",
      width: 120,
      align: "right",
      sorter: (a, b) => (Number(a.price) || 0) - (Number(b.price) || 0),
      render: (v) => (Number.isFinite(Number(v)) ? cf.format(Number(v)) : "—"),
    },
    {
      title: "Total",
      key: "total",
      width: 130,
      align: "right",
      sorter: (a, b) => {
        const ta = Number.isFinite(Number(a.total))
          ? Number(a.total)
          : (Number(a.amount) || 0) * (Number(a.price) || 0)
        const tb = Number.isFinite(Number(b.total))
          ? Number(b.total)
          : (Number(b.amount) || 0) * (Number(b.price) || 0)
        return ta - tb
      },
      render: (_, r) => {
        const t = Number(r.total)
        if (Number.isFinite(t)) return cf.format(t)
        const a = Number(r.amount) || 0
        const p = Number(r.price) || 0
        const fallback = a * p
        return fallback ? cf.format(fallback) : "—"
      },
    },
    {
      title: "Arrived",
      dataIndex: "arrivedAt",
      width: 150,
      render: (val) => {
        const d = toDay(val)
        return d?.isValid() ? d.format("DD.MM.YYYY") : <Text type="secondary">—</Text>
      },
      sorter: (a, b) => {
        const da = toDay(a.arrivedAt)?.valueOf() || 0
        const db = toDay(b.arrivedAt)?.valueOf() || 0
        return da - db
      },
      defaultSortOrder: "descend",
    },
    {
      title: "Barcode",
      key: "barcode",
      dataIndex: "barcode",
      render: (v, r) => v || r.ean || <Text type="secondary">—</Text>,
    },
    ...(showMaterial
      ? [
          {
            title: "Material",
            key: "material",
            filters: materialFilters,
            onFilter: (value, record) =>
              (record.materialName || record.material || "").toString() === value,
            render: (_, r) => r.materialName || r.material || <Text type="secondary">—</Text>,
          },
        ]
      : []),
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 200,
      render: (_, record) => (
        <Space>
          <EditIncomingItemDrawer record={record} />
          <Popconfirm
            title="Delete this item?"
            description="This will remove the record from incoming, rolls/inventory, events, and delete the invoice file."
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDelete(record)}>
            <Button size="small" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  const columns = canSeeAll
    ? baseColumns
    : baseColumns.filter((col) => col.dataIndex !== "supplierName")

  const resetFilters = () => {
    setDateRange([dayjs().startOf("day"), dayjs().endOf("day")])
    setSearch("")
  }

  return (
    <div>
      <Row gutter={[15, 15]}>
        <Col span={24}>
          <Title level={4}>Add Item</Title>
          <AddIncomingItem />
        </Col>

        <Col span={24}>
          <Divider />
          <Title level={4}>History</Title>
          <Space size="middle" wrap style={{ width: "100%", justifyContent: "start" }}>
            <Space size="middle" wrap>
              <RangePicker
                allowClear={false}
                value={dateRange}
                onChange={(val) => setDateRange(val)}
                showTime={false}
                format="DD.MM.YYYY"
              />
              <Input
                allowClear
                style={{ width: 240 }}
                prefix={<SearchOutlined />}
                placeholder="Search (name, notes, code…)"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Space>

            <Space>
              <Button icon={<ReloadOutlined />} onClick={resetFilters}>
                Reset
              </Button>
            </Space>
          </Space>
        </Col>

        <Col span={24}>
          <Table
            rowKey={(r) => r.id}
            loading={loading}
            dataSource={visibleRows}
            columns={columns}
            bordered
            size="small"
            pagination={{ pageSize: 50, showSizeChanger: true }}
            summary={(data) => {
              const sumAmount = data.reduce((acc, r) => acc + (Number(r.amount) || 0), 0)
              const sumTotal = data.reduce((acc, r) => {
                if (Number.isFinite(Number(r.total))) return acc + Number(r.total)
                return acc + (Number(r.amount) || 0) * (Number(r.price) || 0)
              }, 0)
              return (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={columns.length - 2} align="right">
                      <strong>Totals:</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right">
                      <strong>{nf.format(sumAmount)}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right">
                      <strong>{cf.format(sumTotal)}</strong>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              )
            }}
          />
        </Col>
      </Row>
    </div>
  )
}
